<script setup lang="ts">
import { ref, computed, nextTick, onMounted } from 'vue'

type Props = {
    dados: [];
    modelValue: string;
    maxHeight?: string;
    width?: string;
};

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue', 'onEnter']);

const posicaoCursorPrompt = ref('');
const showMentions = ref(false);
const textarea = ref(null);
const autocompleteList = ref(null);
const mentionStyle = ref("");
const usouCharMention = ref(false)
const currentFocus = ref(-1)

const sugestoesFiltradas = computed(() => {
    if (usouCharMention.value) {
        return props.dados;
    }
    return props.dados.filter(sugestao =>
        sugestao.titulo.toLowerCase().startsWith(posicaoCursorPrompt.value.toLowerCase())
    );
})

const onInput = (event: any) => {
    emit('update:modelValue', event.target.value)

    textarea.value.style.height = 'auto';
    textarea.value.style.height = `${textarea.value.scrollHeight}px`

    nextTick(() => {
        const cursorPosition = event.target.selectionStart;
        const words = props.modelValue.slice(0, cursorPosition).split(/\s+/);
        posicaoCursorPrompt.value = words[words.length - 1];

        usouCharMention.value = posicaoCursorPrompt.value == "@"

        if ((sugestoesFiltradas.value.length > 0 && posicaoCursorPrompt.value.length >= 2) || posicaoCursorPrompt.value == "@") {
            showMentions.value = true;
            positionMentions();
        } else {
            showMentions.value = false;
        }
    })

}

const onKeydown = (event: any) => {
    if (event.key === ' ' || event.key === 'Escape') {
        showMentions.value = false;
    }

    if (showMentions.value) {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            currentFocus.value++;
            addActive();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            currentFocus.value--;
            addActive();
        }
    };
    if (event.key === 'Enter') {
        event.preventDefault();

        if (!showMentions.value) {
            emit("onEnter")
            return
        }

        if (currentFocus.value > -1) {
            onSelectionChanged(sugestoesFiltradas.value[currentFocus.value].titulo);
        }
    }

}

const addActive = () => {
    if (!sugestoesFiltradas.value.length) return;
    if (currentFocus.value >= sugestoesFiltradas.value.length) currentFocus.value = 0;
    if (currentFocus.value < 0) currentFocus.value = sugestoesFiltradas.value.length - 1;
    scrollIntoView();
}

const onSelectionChanged = (titulo: any) => {

    if (titulo == "") return;

    const cursorPosicao = textarea.value.selectionStart;
    const textoAntesCursor = props.modelValue.slice(0, cursorPosicao);
    const textoDepoisCursor = props.modelValue.slice(cursorPosicao);
    const palavras = textoAntesCursor.split(/\s+/);
    palavras[palavras.length - 1] = `"${titulo}"`;
    const novoTextoAntesCursor = palavras.join(' ') + ' ';
    showMentions.value = false;
    emit('update:modelValue', novoTextoAntesCursor + textoDepoisCursor)
    nextTick(() => {
        textarea.value.focus();
        textarea.value.setSelectionRange(novoTextoAntesCursor.length, novoTextoAntesCursor.length);
    });
};

const scrollIntoView = () => {
    const container = autocompleteList.value;
    if (container == null) return;

    const activeItem = container.querySelector('.autocomplete-active');
    if (activeItem) {
        const itemTop = activeItem.offsetTop;
        const itemBottom = itemTop + activeItem.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;

        if (itemTop < containerTop) {
            container.scrollTop = itemTop;
        } else if (currentFocus.value < 3) {
            container.scrollTop = 0;
        } else if (currentFocus.value > props.dados.length - 2) {
            container.scrollTop = container.clientHeight + 200;
        } else if (itemBottom > containerBottom) {
            container.scrollTop = itemBottom - container.clientHeight;
        }
    }
}

const positionMentions = () => {
    nextTick(() => {
        const textareaElement = textarea.value;
        const cursorPosition = textareaElement.selectionStart;

        //Cria div e span auxiliar
        const divPai = document.getElementsByClassName("prompt-textbox")[0];
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.whiteSpace = 'pre-wrap';
        div.style.visibility = 'hidden';
        div.style.width = `${textareaElement.clientWidth}px`;
        div.textContent = textareaElement.value.slice(0, cursorPosition).replace(/\n$/, '\n\u200b');
        divPai.appendChild(div);

        const span = document.createElement('span');
        span.textContent = '\u200b';
        div.appendChild(span);

        // Obtem informações de posicionamento e tamanho
        const spanRect = span.getBoundingClientRect();
        const containerRect = textareaElement.getBoundingClientRect();
        const containerPromptRect = divPai.getBoundingClientRect();
        const mentionWidth = document.getElementById("modalDataGrid")?.clientWidth;
        const mentionHeight = document.getElementById("modalDataGrid")?.clientHeight;

        // Pega valores relativos
        const relativeTop = spanRect.top - containerRect.top;
        const relativeLeft = spanRect.left - containerRect.left;

        // posições do mention baseado em valores relativos e de scroll
        let mentionTop = (relativeTop - textareaElement.scrollTop - textareaElement.offsetTop - mentionHeight);
        let mentionLeft = relativeLeft + textareaElement.scrollLeft + textareaElement.offsetLeft;

        // Verifica o tamanho do container para o mention acompanhar cursor horizontalmente
        if (mentionLeft + mentionWidth > containerPromptRect.width)
            mentionLeft = containerPromptRect.width - mentionWidth;

        // Seta valores de stylo para o mention
        mentionStyle.value = `top: ${mentionTop}px !important; left: ${mentionLeft}px !important`;

        // Exclui elementos do DOM
        div.removeChild(span);
        divPai.removeChild(div);
    });
}

onMounted(() => {
    document.addEventListener("click", (event: any) => {
        showMentions.value = false;
    })
})
</script>

<template>
    <textarea autocomplete="off" class="textarea-autocomplete"
        :style="{ maxHeight: maxHeight, width: width }" ref="textarea" id="textAreaPromptIA" :value="props.modelValue"
        @input="onInput" @keydown="onKeydown" placeholder="Escreva sua pergunta para ia...">
    </textarea>
    <div class="container-textarea-autocomplete" v-if="showMentions" id="modalDataGrid" :style="mentionStyle">
        <div class="header-container w-full flex justify-center">
            <div
                class="w-[92%] pt-5 pb-2 flex justify-between items-center border-b border-solid border-[#9CA3AF] text-bold">
                <p>Sugestões de campos</p>
            </div>
        </div>
        <ul v-if="sugestoesFiltradas.length" class="autocomplete-list" id="objetoDataGridAutoCompleteTextarea"
            ref="autocompleteList">
            <div>
                <li v-for="(option, index) in sugestoesFiltradas" :key="index" class="autocomplete-item mx-3"
                    :class="{ 'autocomplete-active': index === currentFocus }"
                    @click="onSelectionChanged(option.titulo)">
                    {{ option.titulo }}
                </li>
            </div>
        </ul>
    </div>
    <slot></slot>
    <slot name="after"></slot>
</template>
<style>
#objetoDataGridAutoCompleteTextarea {
    max-height: 200px !important;
    overflow-y: auto;
    z-index: 50;

    .dx-scrollable-scroll-content {
        background-color: #6FAD28 !important;
    }
}
</style>

<style scoped lang="scss">
.textarea-autocomplete {
    @apply focus:shadow-none focus:ring-transparent focus:outline-0;
    @apply bg-transparent w-full h-full pr-2;
    resize: none;
}

.container-textarea-autocomplete {
    @apply absolute w-[60%];
}

.header-container {
    border-radius: 10px 10px 0 0;
}

.autocomplete-list {
    @apply dark:text-white text-[#4B5563] bg-[#E5E7EB] dark:bg-[#243944];
    border-radius: 0 0 10px 10px;
}

.autocomplete-item {
    padding: 10px 25px;
    font-size: 14px;
}

.autocomplete-list li:hover,
.autocomplete-active {
    @apply text-black bg-[#F2F2F2] dark:bg-[#16252E] dark:text-white;
    border-radius: 6px;

}
</style>